import { useEffect, useRef } from 'react';

const useVisibilityTracking = <T extends HTMLElement = HTMLElement>(callbackFunction: () => void, threshold: number = 1.0) => {
    const elementRef = useRef<T>(null);
    // Keep the latest callback in a ref so an unstable (inline) callback does not
    // re-run the effect and recreate the observer, which would re-fire on re-render.
    const callbackRef = useRef(callbackFunction);
    callbackRef.current = callbackFunction;
    // Guard so the callback fires only once per appearance of the element.
    const hasFiredRef = useRef(false);

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasFiredRef.current) {
                    hasFiredRef.current = true;
                    callbackRef.current();
                }
            },
            { threshold }
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, [threshold]);

    return elementRef;
};

export default useVisibilityTracking;
